import React, { useState, useEffect, useMemo } from "react";
import { Box, Stepper, Step, StepLabel, Link } from "@mui/material";
import ClinicRegistration from "./ClinicRegistration";
import ClinicDetails from "./ClinicDetails";
import Payments from "./Payments";
import styles from "./styles/styles.module.css";
import Logo from '../../app/assets/logo.png';
import { useLocation, useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import { axiosProxyInstance } from "../../utils/request";
import { clinicRegistration } from "../../JsonForms/ClinicRegistration";

const OnboardingStepper = ({stripePromise}) => {
  const [step, setStep] = useState(0);
  const [savedStep, setSavedStep] = useState(0);
  const [clinicRegistrationData, setClinicRegistrationData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const ALL_SPECIALITY_OPTIONS = useMemo(() => (
      clinicRegistration.sections.find(
        (value) => value.sectionName === 'Area of Speciality'
      )?.fields[0]?.options
    ), []);

  const location = useLocation();
  const navigate = useNavigate();
  let clinicId = localStorage.getItem("LPCurrentClinicId");
  const isOnboarding = localStorage.getItem("isOnboarding");

  const fetchDetails = async (id) => {
    try {
      const response = await axiosProxyInstance.get(`/listing/${id}?post_status=all`);
      setDetails(response.data.data ?? []);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch listings");
      setLoading(false);
    }
  };
  useEffect(() => {
    if(clinicId){
      fetchDetails(clinicId);
    }
  }, [clinicId, step]);

  useEffect(() => {
    if(details){
      let physiotherapistsCount;
      const num_workers = details?.data?.num_workers ? parseInt(details?.data?.num_workers) : null;
      if (num_workers == 1) {
          physiotherapistsCount = "1-2";
      }
      if (num_workers == 3) {
          physiotherapistsCount = "3-4";
      }
      if (num_workers == 5) {
          physiotherapistsCount = "5+";
      }else{}

      const serviceKeys = details?.data?.services_v2 ? Object.values(details.data.services_v2) :null;
      const treatmentValues = details?.data?.treatments_v2 ? Object.values(details.data.treatments_v2) : null;

      const mappedValues = {};

      if(serviceKeys?.length){
        serviceKeys.forEach(service => {
          const options = ALL_SPECIALITY_OPTIONS?.[service]?.options || [];
          const matched = treatmentValues?options.filter(opt => treatmentValues.includes(opt)):[];
          if (matched.length) {
            mappedValues[service] = matched;
          }
        });
      }

      let slots = [];
      const defaultOpeningHours = {};
      if (details?.data?.opening_hours?.length) {
          slots = details.data.opening_hours.map((item, index) => ({
              id: `timeSlot-${index + 1}`,
              from: item.from,
              to: item.to,
              days: item.days,
          }));
          slots.forEach((slot) => {
              defaultOpeningHours[`openingTime-${slot.id}`] = slot.from;
              defaultOpeningHours[`closingTime-${slot.id}`] = slot.to;
              defaultOpeningHours[`daysOfWeek-${slot.id}`] = slot.days;
          });
      }
      let detailsObj = {
        title: details?.post_title,
        address_1: details?.data?.address_1,
        address_2: details?.data?.address_2,
        town: details?.data?.location?.[0],
        postcode: details?.data?.postcode,
        telephone_1: details?.data?.phone?.raw,
        num_workers: physiotherapistsCount,
        selectedSpecialization: mappedValues,
        booking_management_software: details?.data?.booking_management_software,

        availableParking: Object.values(details?.data?.parking || {}),
        accessibleAccess: details?.data?.accessible_access,
        insuranceAccepted: details?.data?.health_insurances_accepted ? Object.values(details.data.health_insurances_accepted) : [],
        clinicDescription: details?.data?.description,

        logo: details?.data?.logo,
        openingHours: details?.data?.opening_hours,
        initial_assessment: Object.values(details?.data?.initial_assessment || []),
        follow_up_treatment: Object.values(details?.data?.follow_up_treatment || []),
        initial_appointment_time: details?.data?.initial_appointment_time,
        initial_appointment_cost: details?.data?.initial_appointment_cost,
        followup_appointment_time: details?.data?.followup_appointment_time,
        followup_appointment_cost: details?.data?.followup_appointment_cost,
        assessmentTypeArray: Object.values(details?.data?.assessment_type || []),
        followUpTypeArray: details?.data?.followup_treatment_type || []
      };

      setClinicRegistrationData(detailsObj);
    }
  }, [details]);
  
  useEffect(() => {
    let currentStep = parseInt(localStorage.getItem("currentCompleted"));
    
    if (isNaN(currentStep) && Object.keys(clinicRegistrationData).length > 0) {
      // currentStep = 1;
    }
    if (!isNaN(currentStep)) {
      setSavedStep(currentStep);
      if (currentStep === 3) {
        if (!location.pathname.includes("/payment-success")) {
          localStorage.removeItem("currentCompleted");
          if(isOnboarding === "yes"){
            localStorage.removeItem("LPCurrentUserId");
          }else{}
          localStorage.removeItem("LPCurrentUserEmail");
          localStorage.removeItem("isOnboarding");
        } else { return }
      } else {
        goToStep(currentStep);
      }
    }
  }, [step]);

  const goToStep = (stepNumber) => {
    localStorage.setItem("currentCompleted", stepNumber);
    setStep(stepNumber);
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return step === 0 ? <ClinicRegistration goToStep={goToStep} clinicRegistrationData={clinicRegistrationData} setClinicRegistrationData={setClinicRegistrationData} /> : <></>;
      case 1:
        return step === 1 ? <ClinicDetails goToStep={goToStep} clinicRegistrationData={clinicRegistrationData} /> : <></>;
      case 2:
        return step === 2 ? <Payments goToStep={goToStep} clinicRegistrationData={clinicRegistrationData} stripePromise={stripePromise} /> : <></>;
      default:
        return <ClinicRegistration goToStep={goToStep} />;
    }
  };

  const handleSaveANdExit = () => {
    localStorage.removeItem("currentCompleted");
    localStorage.removeItem("LPCurrentProvideruid");
    localStorage.removeItem("LPCurrentClinicTitle");
    localStorage.removeItem("LPCurrentClinicId");
    if(isOnboarding === "yes"){
      localStorage.removeItem("LPCurrentUserId");
      localStorage.removeItem("LPCurrentUserEmail");
      localStorage.removeItem("LPCurrentUserName");
      localStorage.removeItem("selectedPlan");
      localStorage.removeItem("isOnboarding");
      navigate('/signup');
    }else{
      navigate('/practitioner/listing');
    }
  };

  const stepTitles = ["Clinic Registration", "Clinic Details", "Payment & Billing"];

  return (
    <Box className={styles.stepperContainer}>
      <Box className={styles.stepperSteps}>
        <Box className={styles.logoContainer}>
          <img src={Logo} alt="local-physio" height={18} width={150} />
        </Box>
        <Box className={styles.stepperWrapper}>
          <Stepper activeStep={step} className={styles.stepperWrapperStep}>
            {stepTitles.map((title, index) => (
              <Step
                key={index}
                // onClick={() =>
                //   index >= savedStep ? goToStep(index) : null
                // }
                completed={index <= savedStep}
              >
                <StepLabel
                  StepIconComponent={() => null}
                  classes={{
                    label:
                      step === index
                        ? styles.stepLabelActive
                        : index < savedStep
                          ? styles.stepLabelCompleted
                          : styles.stepLabel,
                    iconContainer: styles.step,
                  }}
                  sx={{
                    '& .MuiStepLabel-label': {
                      color: index <= savedStep ? 'text.primary' : 'text.secondary',
                      opacity: 1, // remove disabled dimming
                    }
                  }}
                >
                  <span className="flex justify-center">
                    <>
                      {index < savedStep ? (
                        <CheckIcon className={styles.checkIcon} />
                      ) : (
                        `${index + 1}.`
                      )}
                    </>
                    {step === index ? (
                      <span>&nbsp;{title}</span>
                    ) : <Box sx={{ display: { xs: "none", sm: "block" } }}>&nbsp;{title}</Box>}
                  </span>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        {
          step === 2
          ?(
            <Link sx={{ display: { xs: "none", sm: "block" } }} className={styles.saveExit} onClick={() => handleSaveANdExit()}>
              Save & Exit
            </Link>
          ):<span></span>
        }
      </Box>
      <Box sx={{ marginTop: 4 }}>{renderStep()}</Box>
      {
          step === 2
          ?(
            <Link sx={{ display: { xs: "block", sm: "none" } }} className={`text-center margin-top-10 ${styles.saveExit}`} onClick={() => handleSaveANdExit()}>
              Save & Exit
            </Link>
          ):<span></span>
        }
    </Box>
  );
};

export default OnboardingStepper;
